<div class="p-6 bg-slate-50 dark:bg-slate-900 min-h-screen font-['Lato',sans-serif]">
    <!-- Decisions on the table -->
    <section class="mb-5">
        <header class="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-4">
            <h2 class="text-[10.5px] font-[700] text-purple-700 uppercase tracking-[1.4px] m-0"><span
                    class="font-['Lato',sans-serif] text-[20px] font-bold tracking-[-0.4px] text-slate-900 dark:text-white capitalize normal-case">Decisions
                    on the Table</span></h2>
            <div class="flex items-center gap-3 flex-wrap justify-end">
                <app-date-picker [size]="'md'" (valueChange)="rangeValues($event)"
                    (dateRangeChange)="onDateRangeChange($event)" [showComparison]="true" [showGroupBy]="true"
                    [allowedGroupByUnits]="allowedGroupByUnits">
                </app-date-picker>
            </div>
        </header>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch relative">
            @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
            <!-- Single-source concentration -->
            <div
                class="bg-white dark:bg-slate-800 pt-[18px] pr-[18px] pb-[16px] pl-[22px] rounded-xl border border-red-200 dark:border-red-900/40 shadow-sm relative overflow-hidden flex flex-col h-full">
                <div class="absolute top-0 left-0 w-1 h-full bg-red-500"></div>
                <div class="flex items-center gap-2 mb-[11px]">
                    <div class="w-2 h-2 rounded-full bg-red-500"></div>
                    <span class="text-[10.5px] font-[700] tracking-[0.7px] text-red-500 uppercase">Single-source
                        concentration</span>
                </div>
                <p class="text-[14.5px] font-[500] leading-[1.5] text-slate-800 dark:text-slate-200 mb-[5px] h-[44px]">
                    <strong class="font-[700] text-black dark:text-white">{{ (decisionSource()?.pct_of_book || 0) |
                        number }}% </strong> of the book ({{ decisionSource()?.account_count || 0 | number
                    }}) is
                    {{ decisionSource()?.source_name || 'Recuvery-direct' }}; only <strong
                        class="text-black dark:text-white">{{ (decisionSource()?.referred_pct_of_book || 0) |
                        number }}% </strong> is referred from
                    others.
                </p>
                <div class="border-t border-dashed border-slate-200 dark:border-slate-700 my-3"></div>
                <div class="text-[9.5px] font-[700] tracking-[0.7px] text-slate-500 uppercase mb-[5px]">Action</div>
                <p class="text-[12.5px] leading-[1.5] text-slate-600 dark:text-slate-400 mb-0">Automate default routing
                    from partner platforms into Recuvery instead of manual handoff.</p>
            </div>

            <!-- Recovery activation lever -->
            <div
                class="bg-white dark:bg-slate-800 pt-[18px] pr-[18px] pb-[16px] pl-[22px] rounded-xl border border-amber-200 dark:border-amber-900/40 shadow-sm relative overflow-hidden flex flex-col h-full">
                <div class="absolute top-0 left-0 w-1 h-full bg-amber-500"></div>
                <div class="flex items-center gap-2 mb-[11px]">
                    <div class="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span class="text-[10.5px] font-[700] tracking-[0.7px] text-amber-500 uppercase">Recovery activation
                        lever</span>
                </div>
                <p class="text-[14.5px] font-[500] leading-[1.5] text-slate-800 dark:text-slate-200 mb-[5px] h-[44px]">
                    <strong class="font-[700] text-black dark:text-white">{{ $safeNavigationMigration(decisionRecovery()?.contacted_count) |
                        number }}</strong> accounts contacted, only
                    <strong class="text-black dark:text-white">{{ $safeNavigationMigration(decisionRecovery()?.paid_off_count) | number
                        }}</strong> paid off, a <strong class="text-black dark:text-white">{{
                        ($safeNavigationMigration(decisionRecovery()?.contact_to_recovery_rate)) | number:'1.0-0' }}%</strong>
                    contact-to-recovery rate.
                </p>
                <div class="border-t border-dashed border-slate-200 dark:border-slate-700 my-3"></div>
                <div class="text-[9.5px] font-[700] tracking-[0.7px] text-slate-500 uppercase mb-[5px]">Action</div>
                <p class="text-[12.5px] leading-[1.5] text-slate-600 dark:text-slate-400 mb-0">Lift {{
                    $safeNavigationMigration(decisionRecovery()?.contact_to_recovery_rate) | number:'1.0-0' }}% → 45% via
                    follow-up sequencing on the {{ decisionRecovery()?.stage_name }} stage, only <strong class="text-black dark:text-white">{{
                        $safeNavigationMigration(decisionRecovery()?.in_progress_count) | number }}</strong> still
                    in progress.
                </p>
            </div>
            <!-- Pipeline throughput gap -->
            <div
                class="bg-white dark:bg-slate-800 pt-[18px] pr-[18px] pb-[16px] pl-[22px] rounded-xl border border-amber-200 dark:border-amber-900/40 shadow-sm relative overflow-hidden flex flex-col h-full">
                <div class="absolute top-0 left-0 w-1 h-full bg-amber-500"></div>
                <div class="flex items-center gap-2 mb-[11px]">
                    <div class="w-2 h-2 rounded-full bg-amber-500"></div>
                    <span class="text-[10.5px] font-[700] tracking-[0.7px] text-amber-500 uppercase">Pipeline throughput
                        gap</span>
                </div>
                <p class="text-[14.5px] font-[500] leading-[1.5] text-slate-800 dark:text-slate-200 mb-[5px] h-[44px]">
                    {{ decisionPipeline()?.stage_name || 'Notification' }} is {{
                    stageTrendLabel($safeNavigationMigration(decisionPipeline()?.mom_growth_pct)) }}
                    <strong class="font-[700] text-black dark:text-white">({{ (decisionPipeline()?.mom_growth_pct ||
                        0) > 0 ? '+' : '' }}{{$safeNavigationMigration(decisionPipeline()?.mom_growth_pct) |
                        number:'1.0-2' }}%)</strong> while {{ decisionPipeline()?.next_stage_name || 'Collection' }},
                    the next
                    stage, is {{ stageTrendLabel($safeNavigationMigration(decisionPipeline()?.next_mom_growth_pct)) }}
                    <strong class="font-[700] text-black dark:text-white">({{ (decisionPipeline()?.next_mom_growth_pct
                        || 0) > 0 ? '+' : '' }}{{$safeNavigationMigration(decisionPipeline()?.next_mom_growth_pct) | number:'1.0-2'
                        }}%)</strong>.
                </p>
                <div class="border-t border-dashed border-slate-200 dark:border-slate-700 my-3"></div>
                <div class="text-[9.5px] font-[700] tracking-[0.7px] text-slate-500 uppercase mb-[5px]">Action</div>
                <p class="text-[12.5px] leading-[1.5] text-slate-600 dark:text-slate-400 mb-0">Audit throughput into
                    Collection: check escalation triggers and team capacity.</p>
            </div>
        </div>
    </section>

    <!-- Where the book stands -->
    <section class="mb-5">
        <h2
            class="font-['Lato',sans-serif] text-[20px] font-bold tracking-[-0.4px] text-slate-900 dark:text-white capitalize normal-case mb-5">
            Where the recovery book stands</h2>

        <div class="grid grid-cols-1 md:grid-cols-5 gap-4 relative">
            @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
            <!-- Total Debt Recovered -->
            <div
                class="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 relative overflow-hidden">
                <div class="flex justify-between items-start mb-2">
                    <h3 class="text-[11px] font-[600] text-[#6a7c93] uppercase">Total Debt Recovered </h3>
                    <span class="text-[9.5px] font-bold px-2 py-1 rounded-full"
                        [ngClass]="(debtRecovered()?.mom_growth_pct ?? 0) > 0  ? 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300' :(debtRecovered()?.mom_growth_pct ?? 0) == 0 ? 'bg-emerald-100 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'">{{
                        (debtRecovered()?.mom_growth_pct ?? 0) > 0 ?
                        'LEADING' : (debtRecovered()?.mom_growth_pct ?? 0) == 0 ? 'STABLE' : 'LAGGING' }}</span>
                </div>
                <div
                    class="font-['Lato',sans-serif] text-[26px] font-normal leading-[1] tracking-[-1px] text-slate-900 dark:text-white mb-2">
                    {{ $safeNavigationMigration(debtRecovered()?.total_recovered_amt) | currency:'USD':'symbol':'1.0-0' }}</div>
                <div class="text-[11.5px] leading-[1.45] text-slate-500 mb-4">
                    @if (debtRecovered()?.by_source?.length) {
                    @for (src of debtRecovered().by_source; track src.source; let last = $last) {
                    {{ src.source }} {{ src.amount | currency:'USD':'symbol':'1.0-0' }}{{ !last ? ' · ' : '' }}
                    }
                    } @else {
                    Recuvery $486K · Lendee $87K · Denefits $56K · Credee $14K
                    }
                </div>
                <div class="text-[11px] font-[500] flex items-center gap-1"
                    [ngClass]="(debtRecovered()?.mom_growth_pct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'">
                    <div class="w-1.5 h-1.5 rounded-full"
                        [ngClass]="(debtRecovered()?.mom_growth_pct ?? 0) >= 0 ? 'bg-emerald-500' : 'bg-red-500'">
                    </div> {{(debtRecovered()?.mom_growth_pct ?? 0) > 0 ? '+' : '' }}{{
                    debtRecovered()?.mom_growth_pct ?? 0 | number:'1.1-2' }}% {{ comparisonLabel }}
                </div>
            </div>
            <!-- Paid Off -->
            <div
                class="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 relative overflow-hidden">
                <div class="flex justify-between items-start mb-2">
                    <h3 class="text-[11px] font-[600] text-[#6a7c93] uppercase">Paid Off </h3>
                    <span class="text-[9.5px] font-bold px-2 py-1 rounded-full"
                        [ngClass]="(accountsPaidOff()?.mom_growth_pct ?? 0)  > 0  ? 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300' :(accountsPaidOff()?.mom_growth_pct ?? 0)   == 0 ? 'bg-emerald-100 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'">{{
                        (accountsPaidOff()?.mom_growth_pct ?? 0) > 0 ?
                        'LEADING' : (accountsPaidOff()?.mom_growth_pct ?? 0) == 0 ? 'STABLE' : 'LAGGING' }}</span>
                </div>
                <div
                    class="font-['Lato',sans-serif] text-[26px] font-normal leading-[1] tracking-[-1px] text-slate-900 dark:text-white mb-2">
                    {{ $safeNavigationMigration(accountsPaidOff()?.paid_off_count) | number }}</div>
                <div class="text-[11.5px] leading-[1.45] text-slate-500  mb-4">Accounts fully recovered · {{
                    $safeNavigationMigration(accountsPaidOff()?.settled_count) | number }}
                    settled · {{ $safeNavigationMigration(accountsPaidOff()?.cancelled_count) | number }} cancelled · of {{
                    $safeNavigationMigration(accountsPaidOff()?.book_total) | number }} book
                </div>
                <div class="text-[11px] font-[500] flex items-center gap-1"
                    [ngClass]="(accountsPaidOff()?.mom_growth_pct ?? 0)  >= 0 ? 'text-emerald-600' : 'text-red-600'">
                    <div class="w-1.5 h-1.5 rounded-full"
                        [ngClass]="(accountsPaidOff()?.mom_growth_pct ?? 0)  >= 0 ? 'bg-emerald-500' : 'bg-red-500'">
                    </div> {{(accountsPaidOff()?.mom_growth_pct ?? 0) > 0 ? '+' : '' }}{{
                    accountsPaidOff()?.mom_growth_pct ?? 0 | number:'1.1-2' }}% {{ comparisonLabel }}
                </div>
            </div>
            <!-- Active Portfolio -->
            <div
                class="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 relative overflow-hidden">
                <div class="flex justify-between items-start mb-2">
                    <h3 class="text-[11px] font-[600] text-[#6a7c93] uppercase">Active Portfolio </h3>
                    <span class="text-[9.5px] font-bold px-2 py-1 rounded-full"
                        [ngClass]="(activePortfolio()?.mom_growth_pct ?? 0) > 0  ? 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300' :(activePortfolio()?.mom_growth_pct ?? 0)  == 0 ? 'bg-emerald-100 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'">{{
                        (activePortfolio()?.mom_growth_pct ?? 0) > 0 ?
                        'LEADING' : (activePortfolio()?.mom_growth_pct ?? 0) == 0 ? 'STABLE' : 'LAGGING' }}</span>
                </div>
                <div
                    class="font-['Lato',sans-serif] text-[26px] font-normal leading-[1] tracking-[-1px] text-slate-900 dark:text-white mb-2">
                    {{ $safeNavigationMigration(activePortfolio()?.active_total) | number }}</div>
                <div class="text-[11.5px] leading-[1.45] text-slate-500  mb-4">Accounts in recovery ·
                    @if (activePortfolio()?.by_source?.length) {
                    @for (src of activePortfolio().by_source; track src.source; let last = $last) {
                    {{ src.source }} {{ src.count | number }}{{ !last ? ' · ' : '' }}
                    }
                    } @else {
                    Recuvery 6,077 · Lendee 452 · Denefits 80 · Credee 38
                    }
                </div>
                <div class="text-[11px] font-[500] flex items-center gap-1"
                    [ngClass]="(activePortfolio()?.mom_growth_pct ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'">
                    <div class="w-1.5 h-1.5 rounded-full"
                        [ngClass]="(activePortfolio()?.mom_growth_pct ?? 0) >= 0 ? 'bg-emerald-500' : 'bg-red-500'">
                    </div> {{(activePortfolio()?.mom_growth_pct ?? 0) > 0 ? '+' : '' }}{{
                    activePortfolio()?.mom_growth_pct ?? 0 | number:'1.1-2' }}% {{ comparisonLabel }}
                </div>
            </div>
            <!-- Total Contracts -->
            <div
                class="bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-100 dark:border-slate-700 relative overflow-hidden">
                <div class="flex justify-between items-start mb-2">
                    <h3 class="text-[11px] font-[600] text-[#6a7c93] uppercase">Total Contracts </h3>
                    <span class="text-[9.5px] font-bold px-2 py-1 rounded-full"
                        [ngClass]="(totalContracts()?.mom_growth_pct ?? 0) >= 0  ? 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300' :(totalContracts()?.mom_growth_pct ?? 0)  == 0 ? 'bg-emerald-100 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'">{{
                        (totalContracts()?.mom_growth_pct ?? 0) > 0 ?
                        'LEADING' : (totalContracts()?.mom_growth_pct ?? 0) == 0 ? 'STABLE' : 'LAGGING' }}</span>
                </div>
                <div
                    class="font-['Lato',sans-serif] text-[26px] font-normal leading-[1] tracking-[-1px] text-slate-900 dark:text-white mb-2">
                    {{ $safeNavigationMigration(totalContracts()?.total_contracts) | number }}</div>
                <div class="text-[11.5px] leading-[1.45] text-slate-500   mb-4">Across {{
                    $safeNavigationMigration(totalContracts()?.business_count) | number }} businesses <br />
                    @if (totalContracts()?.by_source?.length) {
                    @for (src of totalContracts().by_source; track src.source_name; let last = $last) {
                    {{ src.source_name }} {{ src.count | number }}{{ !last ? ' · ' : '' }}
                    }
                    } @else {
                    No data available
                    }
                </div>
                <div class="text-[11px] font-[500] flex items-center gap-1"
                    [ngClass]="(totalContracts()?.mom_growth_pct ?? 0)  >= 0 ? 'text-emerald-600' : 'text-red-600'">
                    <div class="w-1.5 h-1.5 rounded-full"
                        [ngClass]="(totalContracts()?.mom_growth_pct ?? 0) >= 0 ? 'bg-emerald-500' : 'bg-red-500'">
                    </div> {{(totalContracts()?.mom_growth_pct ?? 0) > 0 ? '+' : '' }}{{
                    totalContracts()?.mom_growth_pct ?? 0 | number:'1.1-2' }}% {{ comparisonLabel }}
                </div>
            </div>

            <!-- Recovery Rate -->
            <div
                class="bg-gradient-to-br from-purple-50 from-0% to-white to-70% dark:from-slate-800 dark:to-slate-900 pt-[17px] px-[18px] pb-[16px] rounded-xl shadow-[0_8px_26px_rgba(124,58,237,0.13)] border-[1.5px] border-purple-200 dark:border-purple-900 border-l-[4px] border-l-purple-600 relative overflow-hidden">
                <div class="flex justify-between items-start mb-[11px]">
                    <h3 class="text-[11px] font-[600] text-[#6a7c93] uppercase">Recovery Rate</h3>
                    <span class="text-[9.5px] font-bold px-2 py-1 rounded-full"
                        [ngClass]="(recoveryRate()?.mom_pp_change ?? 0) > 0  ? 'bg-slate-100 text-slate-600 dark:bg-slate-700/60 dark:text-slate-300' :(recoveryRate()?.mom_pp_change ?? 0)  == 0 ? 'bg-emerald-100 text-emerald-500 dark:bg-emerald-900/30 dark:text-emerald-400' :'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'">{{
                        (recoveryRate()?.mom_pp_change ?? 0) > 0 ?
                        'LEADING' : (recoveryRate()?.mom_pp_change ?? 0) == 0 ? 'STABLE' : 'LAGGING' }}</span>
                </div>
                <div
                    class="font-['Lato',sans-serif] text-[26px] font-normal leading-[1] tracking-[-1px] text-slate-900 dark:text-white mb-2">
                    {{ $safeNavigationMigration(recoveryRate()?.recovery_rate_pct) | number:'1.1-2' }}%</div>
                <div class="text-[11.5px] leading-[1.45] text-slate-500  mb-4">{{ $safeNavigationMigration(recoveryRate()?.paid_count) |
                    number }} of {{ $safeNavigationMigration(recoveryRate()?.book_total) | number }} accounts
                </div>
                <div class="text-[11px] font-[500] flex items-center gap-1"
                    [ngClass]="(recoveryRate()?.mom_pp_change ?? 0) >= 0 ? 'text-emerald-600' : 'text-red-600'">
                    <div class="w-1.5 h-1.5 rounded-full"
                        [ngClass]="(recoveryRate()?.mom_pp_change ?? 0) >= 0 ? 'bg-emerald-500' : 'bg-red-500'">
                    </div>{{(recoveryRate()?.mom_pp_change ?? 0) > 0 ? '+' : '' }}{{ recoveryRate()?.mom_pp_change ?? 0 |
                    number:'1.1-2' }}pp {{ comparisonLabel }}
                </div>
            </div>
        </div>
    </section>

    <!-- Cash Up Front Section -->
    <section class="mb-5">
        <h3
            class="font-['Lato',sans-serif] text-[20px] font-bold tracking-[-0.4px] text-slate-900 dark:text-white normal-case mb-5">
            The recovery journey
        </h3>

        <div class="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch relative">
            <!-- Left side cards -->
            <div class="md:col-span-7 flex flex-col gap-4">
                @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
                <div
                    class="bg-white dark:bg-slate-800 p-[18px] rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm relative overflow-visible flex flex-col justify-between group">
                    <div class="flex justify-between items-start mb-2">
                        <h4 class="text-[14px] font-[600] text-[#0e1b2e] dark:text-white">Debt Recovery
                        </h4>
                        <span
                            class="text-[9.5px] font-[700] text-gray-600 dark:text-slate-400 px-2 py-1 rounded-md uppercase tracking-wider">{{$safeNavigationMigration(recoveryJourney()?.total_accounts)
                            | number}}
                            accounts · {{$safeNavigationMigration(recoveryJourney()?.total_debt_amount) | currency }} active exposure</span>
                    </div>
                    <!-- chart -->
                    <!-- <div class="flex w-full h-7.5 rounded-md overflow-hidden mb-5 ">
                        @for(data of recoveryJourney()?.stageBreakdown; track data.key){
                        <div class="h-full border-r-2 border-r-white" [style.width.%]="data.pct_of_book" [ngClass]="{
                            'bg-[#6a7c93]': data.key === 'new_account',
                            'bg-[#7c3aed]': data.key === 'notification',
                            'bg-[#d97706]': data.key === 'collection',
                            'bg-[#16a34a]': data.key === 'resolution'
                            }"
                            [style.background]="data.key === 'dispute' ? 'repeating-linear-gradient(135deg, color-mix(in srgb, #6a7c93 36%, transparent) 0 3px, transparent 3px 7px), #f6f9fe' : null">
                        </div>
                        }
                        </div> -->
                    <div class="relative w-full h-7.5 mb-5 overflow-visible">
                        <div class="flex h-7.5 w-full rounded-md bg-slate-100">
                            @for(data of recoveryJourney()?.stageBreakdown; track data.key; let isFirst = $first; let
                            isLast = $last) {
                            @if(data.pct_of_book > 0) {
                            <!-- Added named group: group/bar -->
                            <div class="relative group/bar h-full border-r-2 border-r-white last:border-r-0 cursor-pointer"
                                [style.flex]="data.pct_of_book + ' 1 0%'"
                                (mouseenter)="hoveredDebtStageKey.set(data.key)"
                                (mouseleave)="hoveredDebtStageKey.set(null)">

                                <!-- Bar Segment -->
                                <div class="h-full w-full transition-opacity duration-150" [class.rounded-l-md]="isFirst" [class.rounded-r-md]="isLast"
                                    [style.opacity]="hoveredDebtStageKey() && hoveredDebtStageKey() !== data.key ? 0.35 : 1"
                                    [ngClass]="{
              'bg-[#6a7c93]': data.key === 'new_account',
              'bg-[#7c3aed]': data.key === 'notification',
              'bg-[#d97706]': data.key === 'collection',
              'bg-[#16a34a]': data.key === 'resolution',
              'bg-dispute-pattern': data.key === 'dispute'
            }" [style.background]="data.key === 'dispute' ? 'repeating-linear-gradient(135deg, color-mix(in srgb, #6a7c93 36%, transparent) 0 3px, transparent 3px 7px), #f6f9fe' : null">
                                </div>

                                <!-- Tooltip bound specifically to group/bar -->
                                <div
                                    class="absolute hidden group-hover/bar:flex flex-col z-50 min-w-[210px] bg-white p-3 rounded-xl shadow-2xl border border-slate-100 pointer-events-none whitespace-nowrap"
                                    [ngClass]="isLast ? 'right-0' : (isFirst ? 'left-0' : 'left-1/2 -translate-x-1/2')">

                                    <!-- Top Row: Icon + Title + Percentage -->
                                    <div class="flex items-center justify-between gap-4">
                                        <div class="flex items-center gap-2">
                                            <span class="w-2.5 h-2.5 rounded-sm" [ngClass]="{
                    'bg-[#6a7c93]': data.key === 'new_account',
                    'bg-[#7c3aed]': data.key === 'notification',
                    'bg-[#d97706]': data.key === 'collection',
                    'bg-[#16a34a]': data.key === 'resolution',
                    'bg-dispute-pattern': data.key === 'dispute'
                  }">
                                            </span>
                                            <span class="text-xs font-bold text-slate-800">{{ data.name }}</span>
                                        </div>
                                        <span class="text-xs font-bold text-slate-900">{{ data.pct_of_book }}%</span>
                                    </div>

                                    <!-- Bottom Row: Subtext -->
                                    <div class="text-[11px] text-slate-500 font-medium mt-1 pl-4">
                                        {{ data.count }} accounts
                                        @if (data.key === 'dispute') {
                                        · outside the active journey
                                        } @else {
                                        · ${{ (data.debt_amount || 0) | number }} exposure
                                        }
                                    </div>

                                </div>

                            </div>
                            }
                            }
                        </div>
                    </div>
                    <div
                        class="grid grid-cols-1 md:grid-cols-4 gap-6 items-stretch relative border-b border-dashed border-slate-200 dark:border-slate-700 pb-4 mb-4">
                        <!-- new account -->
                        @for(data of recoveryJourney()?.stages; track $index){
                        <div class="border-r border-slate-200 dark:border-slate-700"
                            [ngClass]="$last ? 'border-none' : ''">
                            <div class="flex items-center gap-2 mb-3">
                                <div class="w-2.5 h-2.5 rounded-sm "
                                    [ngClass]="data.key == 'new_account'  ? 'bg-[#6a7c93]' : data.key == 'notification' ? 'bg-[#7c3aed]' : data.key == 'collection'  ? 'bg-[#d97706]' : data.key == 'resolution'  ?  'bg-[#16a34a]' : 'bg-[repeating-linear-gradient(135deg,color-mix(in_srgb,#6a7c93_36%,transparent)_0_3px,transparent_3px_7px),#f6f9fe]'">
                                </div>
                                <div class="text-[10.5px] leading-[1.45] text-slate-700 dark:text-slate-300 font-semibold">{{data?.name}}
                                </div>
                                <span class="text-[9.5px] leading-[1.45] text-slate-500 dark:text-slate-400">: {{ data?.keyword }}</span>
                            </div>
                            <div
                                class="text-[26px] font-semibold leading-[1] tracking-[-1px] text-slate-900 dark:text-white mb-2">
                                {{data?.pct_of_book || 0}}%
                            </div>
                            <div class="text-[11.5px] leading-[1.45] text-slate-700 dark:text-slate-300 mb-1">
                                {{data?.debt_amount || 0 | currency}} · {{data?.count || 0}} accts
                            </div>
                            <div class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mb-1">
                                {{data?.advance_rate_pct || 0}}% advance · {{data?.days_in_stage || 0}}d in stage
                            </div>
                        </div>
                        }
                    </div>
                    <p class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mb-2">Hatched tail: <span
                            class="text-purple-600 font-medium">{{recoveryJourney()?.dispute_count || 0}}
                            accounts</span>
                        ({{recoveryJourney()?.dispute_pct || 0}}% of book) held in dispute,
                        outside the active journey. Advance % = share moving on to the next stage before cutoff. </p>
                    @if (recoveryJourney()?.has_attention_data) {
                    <div
                        class="bg-gradient-to-r from-amber-50 to-white dark:from-amber-900/20 dark:to-slate-800 border border-amber-100 dark:border-amber-900/30 border-l-[2px] border-l-amber-600 rounded-xl p-4 mt-3 relative">
                        <div class="text-[9.5px] font-[700] tracking-[0.7px] text-amber-600 dark:text-amber-400 uppercase mb-1">Needs management attention</div>
                        <h4 class="text-[14px] font-[600] text-[#0e1b2e] dark:text-white">{{
                            recoveryJourney()?.attention_amount_compact }} concentrated in
                            {{ recoveryJourney()?.attention_stage_name }} ({{ recoveryJourney()?.attention_pct_of_book
                            || 0 }}% of book)</h4>
                        <p class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mt-2">The biggest revenue-at-risk pool
                            sits one decision away from paying or aging out. Only {{
                            recoveryJourney()?.attention_lowest_advance_pct || 0 }}% advance before the cutoff.
                            {{ recoveryJourney()?.attention_longest_stage_name }} carries the longest cycle ({{
                            recoveryJourney()?.attention_longest_days || 0 }} days).</p>
                    </div>
                    }

                </div>

            </div>

            <!-- Right side Donut -->
            <div
                class="md:col-span-5 bg-white dark:bg-slate-800 p-[18px] rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col items-center">
                @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
                <div class="w-full mb-6">
                    <h4 class="text-[14px] font-[600] text-[#0e1b2e] dark:text-white">Contract Lifecycle</h4>
                </div>

                <div class="relative w-full flex items-center my-auto px-4">
                    <div class="relative w-[200px] h-[200px] shrink-0">
                        <app-donut-chart [data]="timeToContractDonut()" [width]="200" [height]="200"
                            [innerRadiusRatio]="0.75" [outerRadiusRatio]="1.0" [radiusScale]="1.0" [showLegend]="false"
                            [showValue]="false" [showPolylines]="false" [expandOnHover]="false"
                            [highlightedLabel]="hoveredContractLabel()"
                            (sliceHover)="hoveredContractLabel.set($event)">
                        </app-donut-chart>

                        <div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                            <span class="text-3xl font-[800] text-slate-900 dark:text-white">{{ timeToContractTotal() |
                                number
                                }}</span>
                            <span
                                class="text-[11px] font-bold text-slate-500 dark:text-slate-400 tracking-wide mt-1 uppercase">Contracts</span>
                        </div>
                    </div>

                    <div class="grid grid-cols-2 gap-x-12 gap-y-8 ml-16 w-full">
                        @for (item of timeToContractDonut(); track item.label) {
                        <div class="flex flex-col cursor-default transition-opacity duration-150"
                            [style.opacity]="hoveredContractLabel() && hoveredContractLabel() !== item.label ? 0.4 : 1"
                            (mouseenter)="hoveredContractLabel.set(item.label)"
                            (mouseleave)="hoveredContractLabel.set(null)">
                            <div class="flex items-center gap-2">
                                <div class="w-2.5 h-2.5 rounded-sm" [style.backgroundColor]="item.color"></div>
                                <span
                                    class="text-[13px] font-[500] text-slate-900 dark:text-slate-200 flex items-center">
                                    {{ item.label }}
                                </span>
                            </div>
                            <div
                                class="font-['Lato',sans-serif] text-[26px] font-normal leading-[1] tracking-[-1px] text-slate-900 dark:text-white mt-1 mb-1 pl-[18px]">
                                {{ item.value}}%</div>
                        </div>
                        }
                    </div>

                </div>

            </div>
        </div>
    </section>

    <!-- What's driving the numbers -->
    <section class="mb-10">
        <h3
            class="font-['Lato',sans-serif] text-[20px] font-bold tracking-[-0.4px] text-slate-900 dark:text-white normal-case mb-5">
            What's driving the numbers</h3>
        <div class="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch relative">
            <!-- Left side cards -->
            <div class="md:col-span-7 flex flex-col gap-4">
                @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
                <div class="bg-white dark:bg-slate-800 p-[18px] rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex-1 flex flex-col">
                    <div class="w-full mb-6">
                        <h4 class="text-[14px] font-[600] text-[#0e1b2e] dark:text-white">Recovery By Origin Source
                        </h4>
                        <p class="text-[11.5px] text-[#6a7c93] dark:text-slate-400 mt-[3px] leading-[1.4]">
                            Recovery rate = paid-off accounts ÷ accounts from that source · ranked best to worst</p>
                    </div>
                    <div class="space-y-3 text-[11.5px] leading-[1.45] mb-2">
                        <!-- Item 1: Denefits -->

                        @for(data of recoverySource()?.sources; track $index){
                        <div class="pb-4 border-b border-slate-100 dark:border-slate-700" [ngClass]="$last ? 'border-none' : ''">
                            <div class="flex items-center justify-between mb-1.5">
                                <div class="flex items-center gap-2">
                                    <span class="w-2.5 h-2.5 rounded-full shrink-0"
                                        [ngClass]="data.source_key == 'denefits'  ? 'bg-emerald-600' : data.source_key == 'credee' ? 'bg-teal-600' : data.source_key == 'lendee'  ? 'bg-blue-600' : 'bg-purple-600'"></span>
                                    <span class="text-[12.5px] font-semibold text-slate-800 dark:text-white">{{data?.source_name}}</span>
                                </div>
                                <div class="flex items-center gap-4 text-[11px]">
                                    <span class="text-slate-500">{{data?.accounts}} accts</span>
                                    <span class="text-[12.5px] font-semibold text-slate-800 dark:text-white">{{$safeNavigationMigration(data?.recovered_amt) |
                                        currency}}</span>
                                </div>
                            </div>
                            <div class="flex items-center justify-between">
                                <div class="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                    <div class=" h-full rounded-full" [style.width.%]="data.recovery_rate_pct"
                                        [ngClass]="data.source_key == 'denefits'  ? 'bg-emerald-600' : data.source_key == 'credee' ? 'bg-teal-600' : data.source_key == 'lendee'  ? 'bg-blue-600' : 'bg-purple-600'">
                                    </div>
                                </div>
                                <div class="text-right pl-5">
                                    <span
                                        class="text-[12.5px] font-semibold text-slate-800 dark:text-white">{{data?.recovery_rate_pct}}%</span>
                                </div>
                            </div>

                        </div>
                        }
                        <!-- @if(isLoading() === false){<div
                                class="flex items-center justify-center gap-2 h-60 text-slate-500">
                                <app-individual-toaster></app-individual-toaster>
                            </div> } -->

                    </div>

                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
                        <!-- Left Inner Card: Referred Accounts -->
                        <div class="bg-slate-100 dark:bg-slate-700/40 rounded-xl border border-slate-200 dark:border-slate-600/50 p-3">
                            <h5 class="text-[11px] font-[600] text-gray-500 dark:text-slate-400 mb-2 uppercase">Referred Accounts</h5>
                            <h3
                                class="text-[17px] font-[600] tracking-[-1px] text-slate-900 dark:text-white mb-2 leading-none">
                                {{recoverySource()?.referred_summary?.recovery_rate_pct}}% recovery rate</h3>
                            <p class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mt-[7px] mb-0">
                                {{recoverySource()?.referred_summary?.accounts}} accounts ·
                                {{recoverySource()?.referred_summary?.pct_of_book}}% of
                                book → <strong
                                    class="text-slate-800 dark:text-white">{{recoverySource()?.referred_summary?.pct_of_cash_recovered}}%</strong>
                                of cash recovered</p>
                        </div>

                        <!-- Right Inner Card: Direct Accounts -->
                        <div class="bg-slate-100 dark:bg-slate-700/40 rounded-xl border border-slate-200 dark:border-slate-600/50 p-3">
                            <h5 class="text-[11px] font-[600] text-gray-500 dark:text-slate-400 mb-2 uppercase">Direct Accounts</h5>
                            <h3
                                class="text-[17px] font-[600] tracking-[-1px] text-slate-900 dark:text-white mb-2 leading-none">
                                {{recoverySource()?.direct_summary?.recovery_rate_pct}}% recovery rate</h3>
                            <p class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mt-[7px] mb-0">
                                {{recoverySource()?.direct_summary?.accounts}} accounts ·
                                {{recoverySource()?.direct_summary?.pct_of_book}}%
                                of book →
                                <strong
                                    class="text-slate-800 dark:text-white">{{recoverySource()?.direct_summary?.pct_of_cash_recovered}}%</strong>
                                of cash recovered
                            </p>
                        </div>
                    </div>
                </div>

            </div>
            <!-- right side cards -->
            <div
                class="md:col-span-5 bg-white dark:bg-slate-800 p-[18px] rounded-xl border border-slate-100 dark:border-slate-700 shadow-sm flex flex-col ">
                @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
                <div class="w-full mb-6">
                    <div class="flex justify-between items-center">
                        <h4 class="text-[14px] font-[600] text-[#0e1b2e] dark:text-white">Platform Fee Revenue by
                            Source</h4>
                        <span
                            class="text-[9.5px] text-emerald-800 bg-emerald-100/50 rounded-full px-2 py-1 font-bold uppercase">
                            {{$safeNavigationMigration(platformFee()?.total_revenue)
                            | currency}}
                            Total
                        </span>
                    </div>
                    <p class="text-[11.5px] text-[#6a7c93] dark:text-slate-400 mt-[3px] leading-[1.4]">
                        Platform fees collected by Recuvery</p>
                </div>
                <div class="space-y-3 text-[11.5px] leading-[1.45]">
                    <!-- Item 1: Recuvery -->
                    @for(data of platformFee()?.by_source; track $index){
                    <div class="pb-4 border-b border-slate-100 dark:border-slate-700 last:border-b-0">
                        <div class="flex items-center justify-between mb-1.5">
                            <div class="flex items-center gap-2">
                                <span class="w-2.5 h-2.5 rounded-full shrink-0"
                                    [ngClass]="data.source_key == 'denefits'  ? 'bg-emerald-600' : data.source_key == 'credee' ? 'bg-teal-600' : data.source_key == 'lendee'  ? 'bg-blue-600' : 'bg-purple-600'"></span>
                                <span class="text-[12.5px] font-semibold text-slate-800 dark:text-white">{{data?.source_name}}</span>
                            </div>
                            <div class="flex items-center gap-4">
                                <span class="text-[12.5px] font-semibold text-slate-800 dark:text-white">{{$safeNavigationMigration(data?.total) | currency}}</span>
                            </div>
                        </div>
                        <div class="flex items-center justify-between">
                            <div class="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                                <div class=" h-full rounded-full" [style.width.%]="data.pct_of_total"
                                    [ngClass]="data.source_key == 'denefits'  ? 'bg-emerald-600' : data.source_key == 'credee' ? 'bg-teal-600' : data.source_key == 'lendee'  ? 'bg-blue-600' : 'bg-purple-600'">
                                </div>
                            </div>
                        </div>
                        <div class="grid grid-cols-2 gap-3 mt-2">
                            <!-- Left Inner Card: Contracts -->
                            <div class="bg-slate-100 dark:bg-slate-700/40 rounded-lg border border-slate-200 dark:border-slate-600/50 p-2">
                                <div class="flex items-center justify-between">
                                    <p class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mb-0">Contracts
                                    </p>
                                    <p class="text-[11.5px] leading-[1.45] text-slate-900 dark:text-white font-semibold mb-0">
                                        {{$safeNavigationMigration(data?.contract_fees) | currency}}
                                    </p>
                                </div>
                            </div>
                            <!-- Right Inner Card: Payoffs -->
                            <div class="bg-slate-100 dark:bg-slate-700/40 rounded-lg border border-slate-200 dark:border-slate-600/50 p-2">
                                <div class="flex items-center justify-between">
                                    <p class="text-[11.5px] leading-[1.45] text-slate-500 dark:text-slate-400 mb-0">Payoffs</p>
                                    <p class="text-[11.5px] leading-[1.45] text-slate-900 dark:text-white font-semibold mb-0">
                                        {{$safeNavigationMigration(data?.payoff_fees) | currency}}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    }
                </div>
            </div>
        </div>
    </section>
    <!-- channel funnel -->
    <section class="space-y-6">
        <div
            class="flex-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 shadow-sm relative overflow-hidden hover:shadow-md transition-shadow">
            @if(isLoading()){ <app-individual-toaster></app-individual-toaster> }
            <h4 class="text-[14px] font-[600] text-[#0e1b2e] dark:text-white">Channel Funnel: Reach To Payment</h4>
            <p class="text-[11.5px] text-slate-500 dark:text-slate-400">Every outreach channel through the full journey: sent → opened →
                attempted → paid · end yield ranks channel effectiveness</p>
            <div class="overflow-x-auto mt-5">
                <table class="w-full text-left border-collapse">
                    <thead>
                        <tr class="border-b border-slate-200 dark:border-slate-700">
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Channel</th>
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Sent</th>
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Opened / Scanned / Answered</th>
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Attempted</th>
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Paid</th>
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                End yield</th>
                            <th class="py-2 px-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                                Rank</th>
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of channelFunnel()?.channels; track row.name; let i = $index) {
                        <tr
                            class="border-b border-slate-100 dark:border-slate-700/50 hover:bg-slate-50 dark:hover:bg-slate-800/80 transition-colors">
                            <td
                                class="py-2 px-3 text-[12.5px] text-slate-900 dark:text-white font-semibold flex items-center">
                                <span class="w-2.5 h-2.5 rounded-full  mr-1.5"
                                    [ngClass]="row.channel_key === 'demand_letter' ? 'bg-teal-600': row.channel_key === 'sms' ? 'bg-purple-600' : row.channel_key === 'email' ? 'bg-blue-600' : 'bg-amber-600'"></span>
                                {{row?.name}}
                            </td>
                            <td class="py-2 px-3 text-[11px] text-slate-900 dark:text-white font-medium">
                                {{row?.sent || 0}}</td>
                            <td class="py-2 px-3 text-[11px] text-slate-700 dark:text-slate-300 font-medium">
                                {{row?.opened || 0}} ({{row?.open_rate_pct || 0}}%)
                            </td>
                            <td class="py-2 px-3 text-[11px] text-slate-700 dark:text-slate-300 font-medium">
                                {{row.channel_key
                                === 'calls' ? '--' : row?.attempted || 0}}</td>
                            <td class="py-2 px-3 text-[11px] text-slate-700 dark:text-slate-300 font-medium">
                                {{row.channel_key
                                === 'calls' ? '--' : row?.paid
                                || 0}}</td>
                            <td class="py-2 px-3 text-[11px] text-slate-700 dark:text-slate-300 font-medium">
                                <span class="text-[11px]" [ngClass]="row.rank === 1 ? 'text-emerald-600': row.rank === 2 ? 'text-emerald-600' : row.rank === null && row.channel_key
                                    === 'calls' ? 'text-slate-400': 'text-red-600'">{{row.channel_key
                                    === 'calls' ? '--' : row?.end_yield_pct || 0 }} {{row.channel_key
                                    === 'calls' ? '' : '%'}}</span>
                            </td>
                            <td class="py-2 px-3 text-[11px] text-slate-700 dark:text-slate-300 font-medium">
                                <span
                                    class="text-emerald-600 bg-emerald-100 rounded-full px-2 py-1 uppercase text-[9.5px] font-bold"
                                    [ngClass]="row.rank === 1 ? 'bg-emerald-100 text-emerald-600': row.rank === 2 ? 'bg-emerald-100 text-emerald-600' : row.rank === null && row.channel_key
                                    === 'calls' ? 'bg-transparent text-slate-400': 'bg-red-100 text-red-600'">{{row.rank
                                    === 1 ? '▲ Best' : row.rank === 2 ? 'Strong' : row.rank === null && row.channel_key
                                    === 'calls' ? '--' : 'lowest'
                                    }}</span>
                            </td>
                        </tr>
                        }
                    </tbody>
                </table>
                @if (channelFunnel()?.channels?.length === 0) {
                <div class="py-8 text-center text-slate-400 text-sm">
                    No summary data available
                </div>
                }
            </div>
            <div class="border-t-[2px] border-dashed border-slate-200/80 dark:border-slate-700/50 pt-4 mt-4">
                @if (channelFunnelSummary()) {
                <p class="text-[11.5px] text-slate-500 dark:text-slate-400">{{ channelFunnelSummary() }}</p>
                }
            </div>
        </div>
    </section>

</div>